import { useCallback } from 'react';
import { HOW_IT_WORKS_SECTIONS } from './sections.js';

interface SectionNavProps {
  activeId: string | null;
  /** 0–1 fraction of the page scrolled. Drives the thin progress rail. */
  progress: number;
}

/**
 * Sticky in-page navigation for the explainer.
 *
 * - A thin vertical progress rail fills as the reader scrolls.
 * - Each section is a button that smooth-scrolls its anchor into view.
 * - The section matching `activeId` (resolved by the page's scroll-spy) is
 *   highlighted.
 *
 * Hidden below `lg` — on narrow screens the page reflows to a single column
 * and the nav is removed from the flow entirely.
 */
export function SectionNav({ activeId, progress }: SectionNavProps) {
  const handleJump = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Move focus to the section for keyboard/AT users without yanking scroll.
    el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }, []);

  const pct = Math.round(progress * 100);

  return (
    <nav
      data-testid="how-it-works-nav"
      aria-label="On this page"
      className="hidden lg:block"
    >
      {/* Spec 250 (review P1-10, P1-15): "On this page" is a sentence-case
          caption, not tracked capitals. The current section is ink text with a
          2px ink rule on its left — it was a tinted block with a Skagen disc.
          The progress rail is that same left line, filling in ink. */}
      <div className="sticky top-6">
        <p className="mb-3 text-xs text-[var(--hub-color-ink-muted)]">
          On this page
        </p>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 w-px bg-[var(--hub-color-line)]" aria-hidden="true">
            <div
              className="absolute left-0 top-0 w-full bg-[var(--hub-color-ink-subtle)] transition-[height] duration-150 ease-out"
              style={{ height: `${pct}%` }}
            />
          </div>
          <ul className="space-y-0.5">
            {HOW_IT_WORKS_SECTIONS.map((section, i) => {
              const isActive = activeId === section.id;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => handleJump(section.id)}
                    aria-current={isActive ? 'true' : undefined}
                    className={`relative flex w-full items-start gap-2.5 border-0 bg-transparent py-2 pl-4 pr-2 text-left transition-colors ${
                      isActive
                        ? 'text-[var(--hub-color-ink)] before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:bg-[var(--hub-color-ink)]'
                        : 'text-[var(--hub-color-ink-muted)] hover:text-[var(--hub-color-ink)]'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className={`block text-sm leading-snug ${isActive ? 'font-semibold' : 'font-medium'}`}>
                        {i + 1}. {section.title}
                      </span>
                      <span className="block truncate text-xs text-[var(--hub-color-ink-muted)]">
                        {section.tagline}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
}
